/**
 * LibraryBackedTable — ship 0.36.671, extended 0.36.672.
 *
 * Materialises a table element whose `source === 'library'`.
 * Fetches library data, projects the admin-picked columns into
 * headers/rows, and delegates rendering to PageTable.
 *
 * Pagination rules (300-row threshold, same as dropdowns):
 *   - Primary library ≤ 300 rows: single `/rows` fetch, client-side
 *     pagination via PageTable + PaginationContext.
 *   - Primary library > 300 rows: SERVER-paginated. Fetches only
 *     the current page. Publishes the library's total to
 *     PaginationContext (`reportRowCount`) so PagePagination shows
 *     the correct page count. PageTable receives already-sliced
 *     rows and is told (via `preSliced`) not to slice again.
 *
 * 0.36.672 — Multi-library joins.
 *
 *   `el.joins` is an array of `{id, library, on_primary, on_joined}`.
 *   Each join pulls a second library and links it to the primary
 *   library via matching column values (`on_primary` in the primary
 *   row equals `on_joined` in the joined library's row).
 *
 *   `el.library_columns` entries carry a `library_ref` field that
 *   names either 'primary' or one of the join ids. Entries without
 *   library_ref are treated as 'primary' (backward-compat with
 *   0.36.671).
 *
 *   Runtime materialisation for each join:
 *     1. Collect the set of primary_row[join.on_primary] values from
 *        the currently-fetched primary rows.
 *     2. Fetch matching rows from the joined library via
 *        /rows?filter_column={join.on_joined}&filter_values=<csv>
 *        — reuses the cascade endpoint added in ship 0.36.658.
 *     3. Build a lookup map { row[on_joined]: row } for that join.
 *     4. For each column entry with library_ref == join.id, pull
 *        the cell value from the lookup map (empty if no match).
 *
 *   This means joined libraries don't need to be small — we only
 *   fetch the specific rows the primary references, not the whole
 *   library.
 *
 * Client-side sort is disabled when in server-paginated mode.
 * Server-side sort + cascade-filter on primary + reference-column
 * pretty-rendering are all queued follow-ups.
 */
import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import PageTable, { filterRequiredRows, lastColRestOf, edgeLinesOf } from './PageTable';
import { PaginationContext } from './PaginationContext';
import { useLanguage } from '../context/LanguageContext';
import { getElementId } from '../utils/format';
import { localizeRow, localizeRows } from '../lib/visitorDates';
import { useFormatVersion } from '../hooks/useFormatVersion';
import Alert from './Alert';

const SERVER_THRESHOLD = 300;
const DEFAULT_PER_PAGE = 10;

export default function LibraryBackedTable({
  el, canvasResize = null, sampleRows = null,
  // 0.36.679 — Resolved filter values, populated by the caller
  // (PageContentRenderer / AdminFormPreview) from current form
  // values. Shape:
  //   [{ library_ref, library_column, values: string[] }, ...]
  // - values.length === 0 for a filter: filter is INACTIVE (source
  //   element has no value) → doesn't narrow anything, matches all.
  // - library_ref === 'primary': filter applies to the primary
  //   library's /rows fetch (server-paginated) OR to client-side
  //   post-filter (small library).
  // - library_ref !== 'primary': join-attached filter. This ship
  //   supports primary-attached filters only; join-attached is a
  //   queued follow-up. The runtime silently ignores those entries.
  filterValues = [],
  // 0.36.682 — Current value of the linked select-all mode source
  // element (a form radio/single-select). Passed by the caller;
  // undefined means no source linked or its value hasn't been
  // set yet. LibraryBackedTable normalises to 'page' or 'all'
  // before handing to PageTable.
  selectionModeValue = null,
  // 0.36.692 — Callback to publish this table's selected rows
  // to form values. Called by PageContentRenderer / AdminFormPreview
  // with (element_id, arrayOfSelectedRowObjects). No-op if
  // el.element_id is empty or this callback isn't provided.
  onSelectionPublish = null,
  // The current text of the Search element this table's search is
  // linked to. null / undefined / empty all mean "no active search"
  // and every row passes, matching the empty-source semantics used
  // for filters.
  searchSourceValue = null,
  // 0.36.700 — Command signal from the form-level command channel.
  // Shape: { command, ts } or null. Passed through to PageTable
  // which owns the selection state and applies the change.
  externalCommand = null,
  // 0.40.06 - Resolver for header text, supplied by the caller
  // (which owns the expression context: values, language, library
  // cache). Lets a column's `header` override be an `=lib(...)` or
  // `=expression` string, which is how a library table gets
  // per-language headers: the library row supplies the right text
  // for the citizen's language. This component deliberately does
  // NOT import the expression engine itself - it has no ctx, and
  // threading one in would duplicate what the caller already has.
  // Defaults to identity so the canvas / any caller without a
  // context keeps rendering the raw string.
  resolveHeader = null,
  // 0.46.20 - The same for CELLS: a library value that is a formula
  // (text starting with "=") is worked out with the page's values. It
  // showed as the formula text.
  // 0.46.21 - And {placeholders} in a cell are filled, as in any other
  // text. Text with no placeholder is left exactly as stored.
  resolveCell = null,
  // 0.48.64 - The Edit link on each row: { header, isIcon, edits, cell }
  // from the page renderer. edits are keyed by the row's id, then by
  // library column; cell(rowObj, key) draws the link.
  rowEdit = null,
}) {
  const {
    library,
    library_columns: pickedColumns = [],
    joins: joinDefs = [],
    sortable,
    checkable,
    width,
    per_page_default,
    search_enabled: searchEnabled = false,
    search_columns: searchColumns = [],
    search_source_element_id: searchSourceElementId = '',
    // 0.40.78 - Section-level "When source is empty". Empty string
    // means "not set", so the per-filter fallback applies.
    empty_source_behavior: emptySourceBehavior = '',
    // 0.40.95 - The same question for SEARCH: with the linked Search
    // element empty, show everything (default) or nothing until the
    // citizen types. Separate from the filters' setting - a table can
    // reasonably want "everything until you filter" with "nothing
    // until you search", or the reverse.
    search_empty_source_behavior: searchEmptyBehavior = '',
    // 0.40.79 - Master switch for the Filters section. UNDEFINED means
    // "not set" and filtering stays ON, so every table saved before
    // this behaves exactly as it did; only an explicit false turns it
    // off. Defaulting to false would have silently unfiltered every
    // existing table on deploy.
    filters_enabled: filtersEnabled,
    // 0.40.75 - selection_id_columns no longer read: identity is
    // always the row id. Existing values on saved tables are
    // simply ignored, so nothing needs migrating.
  } = el;
  const { language } = useLanguage();
  const ctx = useContext(PaginationContext);
  const tableName = getElementId(el);

  // 0.40.94 - A table's search always comes from a linked Search
  // element. The embedded input, its searchInput / searchDebounced
  // state and the 300ms debounce are gone: the Search element (or its
  // consumer) debounces upstream, so debouncing again here only added
  // latency.

  // Effective search terms — trimmed, lowercased for client-side
  // matching. On the wire, we send the original trimmed string; the
  // backend lowercases both sides.
  //
  // 0.36.698 — In linked mode, the source of truth is the external
  // value; internal state is inert (kept in the tree for the legacy
  // fallback path). Coerce nullish external → '' so a not-yet-typed
  // Search element behaves the same as "no search."
  const externalSearchRaw = searchSourceValue == null
    ? '' : String(searchSourceValue);
  const searchTrimmed = externalSearchRaw.trim();
  const searchLower = searchTrimmed.toLowerCase();
  // 0.40.95 - Search's "when source is empty" - show no rows until
  // something is typed. Only meaningful while search is on and the box
  // is empty; once there is a term, the search itself decides.
  const searchBlocksAll = searchEnabled
    && searchTrimmed.length === 0
    && searchEmptyBehavior === 'show_none';
  const searchActive = searchEnabled
    && searchTrimmed.length > 0
    // 0.40.95 - An empty search_columns now means ALL COLUMNS, which
    // is what the backend has always done with the parameter absent
    // (_entry_matches_q: "Empty/None keeps legacy behavior: search
    // across every column"). Only this guard made an empty list mean
    // "no search", so a table with search enabled and no columns
    // ticked silently never matched anything.
    ;

  // --- Primary library /meta ---
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState('');

  useEffect(() => {
    if (!library) return undefined;
    let cancelled = false;
    setMeta(null);
    setMetaError('');
    fetch(`/api/form-libraries/${encodeURIComponent(library)}/meta`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(m => { if (!cancelled) setMeta(m); })
      .catch(err => { if (!cancelled) setMetaError(String(err.message || err)); });
    return () => { cancelled = true; };
  }, [library]);

  // 0.36.682 (hotfix) — Row-count reporting is now done inside the
  // /rows fetch effect below, using the actual post-filter total
  // (server-paginated: data.total from the envelope; small library:
  // the filtered incoming.length). Reporting meta.total_rows here
  // was WRONG for tables with active filters/search — the paginator
  // saw the un-filtered library size and computed too many pages.

  // --- Current pagination state (from context) ---
  const paginationState = ctx?.states?.[tableName];
  const currentPage = paginationState?.currentPage || 1;
  const perPage = paginationState?.perPage
    || Number(per_page_default) || DEFAULT_PER_PAGE;

  const totalRows = meta?.total_rows || 0;
  const serverPaginated = totalRows > SERVER_THRESHOLD;

  // 0.36.703 — Post-filter/search row count. Captured from each
  // /rows fetch. Reflects the count that would be shown if the
  // table were paginated to infinity. Published as
  // `${element_id}__total` alongside the selection.
  const [effectiveTotalRows, setEffectiveTotalRows] = useState(0);

  // --- Primary rows fetch ---
  // 0.44.72 - Rows as sent; date columns formatted on render, so a later
  // format, language or month-name change shows.
  const [primaryRaw, setPrimaryRows] = useState([]);
  const [primaryTypes, setPrimaryTypes] = useState(null);
  const fmtVersion = useFormatVersion();
  const primaryRows = useMemo(
    () => (primaryTypes ? localizeRows(primaryRaw, primaryTypes) : primaryRaw),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [primaryRaw, primaryTypes, fmtVersion]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState('');

  // 0.36.679 — Primary-attached filters. Server-paginated mode
  // appends each filter's column/values to the URL as repeated
  // filter_column / filter_values query pairs (backend joins
  // AND-wise per ship 0.36.679). Small-library (load-all) mode
  // fetches unfiltered and applies the filters client-side after,
  // for zero-latency reactivity on typical dropdown changes.
  //
  // 0.36.679 — Filter values with an empty array meant "source has
  // no value" and used to always be treated as INACTIVE (all rows
  // pass). Matched dropdown cascade behavior at the time.
  //
  // 0.36.687 — Admin now controls this per filter via
  // `empty_source_behavior`. Two values:
  //   'show_all'  (default, back-compat): empty source → filter
  //     INACTIVE, all rows pass
  //   'show_none': empty source → filter matches nothing, table
  //     shows zero rows until the source has a value
  //
  // Legacy entries missing the field default to 'show_all'.
  const activePrimaryFilters = useMemo(() => {
    // 0.40.79 - Off means no filters apply at all, whatever is
    // configured. The rows stay in the panel so nothing is lost when
    // it is switched back on.
    if (filtersEnabled === false) return [];
    const arr = Array.isArray(filterValues) ? filterValues : [];
    return arr
      .filter(f => f
        && (f.library_ref || 'primary') === 'primary'
        && f.library_column)
      .map(f => {
        const hasValues = Array.isArray(f.values) && f.values.length > 0;
        // 0.40.78 - "When source is empty" moved from per-filter to a
        // single setting for the whole Filters section. The per-filter
        // value is still honoured as a FALLBACK so tables saved before
        // the move keep behaving as they did; without that, any filter
        // set to "show none" would have silently flipped to "show all"
        // on deploy.
        const behavior = (emptySourceBehavior || f.empty_source_behavior)
          === 'show_none' ? 'show_none' : 'show_all';
        // Only include filters that either have values, OR are
        // 'show_none' with empty values (which we send to the
        // backend / apply client-side as an empty allowed-set to
        // force zero rows).
        if (hasValues) return f;
        if (behavior === 'show_none') return { ...f, values: [] };
        return null;
      })
      .filter(f => f !== null);
  }, [filterValues, emptySourceBehavior, filtersEnabled]);

  // Stable key so refetch fires only when the effective filters change.
  const activePrimaryFiltersKey = useMemo(
    () => JSON.stringify(activePrimaryFilters.map(f =>
      [f.library_column, [...(f.values || [])].sort(),
       f.empty_source_behavior || 'show_all']
    )),
    [activePrimaryFilters]
  );

  // 0.36.717 — Fetch-payload freeze during search. Search is a
  // spotlight over a fixed row set; if filter sources change while
  // the citizen is searching, we don't refetch — the pre-search
  // filter snapshot stays in effect. When search clears, current
  // filter values apply in one settling fetch.
  //
  // Two state variables snapshot the live filter identity + array:
  //   frozenFilters, frozenFiltersKey — mirror live activePrimaryFilters
  //   / activePrimaryFiltersKey WHEN search is empty, and freeze
  //   (stop updating) once search becomes active. The fetch effect
  //   and paginator-reset effect read from these instead of the
  //   live values.
  //
  // Rules that read the table's published state (${id}, ${id}__*)
  // are unaffected — that publisher fires on selection change
  // independently. Only the fetch is frozen.
  const [frozenFilters, setFrozenFilters] = useState(activePrimaryFilters);
  const [frozenFiltersKey, setFrozenFiltersKey] = useState(activePrimaryFiltersKey);
  useEffect(() => {
    if (!searchTrimmed) {
      setFrozenFilters(activePrimaryFilters);
      setFrozenFiltersKey(activePrimaryFiltersKey);
    }
  }, [activePrimaryFilters, activePrimaryFiltersKey, searchTrimmed]);

  useEffect(() => {
    if (!library || !meta) return undefined;
    let cancelled = false;
    setRowsLoading(true);
    setRowsError('');
    const params = new URLSearchParams();
    if (language) params.set('lang', language);
    // 0.36.676 — Sample mode (canvas preview): fetch only the first
    // N rows and skip pagination entirely.
    if (sampleRows != null && sampleRows > 0) {
      params.set('page', '1');
      params.set('per_page', String(sampleRows));
    } else if (serverPaginated) {
      params.set('page', String(currentPage));
      params.set('per_page', String(perPage));
    }
    // 0.36.679 — Server-side filters (only in server-paginated mode).
    // Small-library mode filters client-side after fetch for latency.
    // 0.36.717 — Uses frozenFilters (see freeze block above) so
    // that filter-source changes during search don't refetch.
    if (serverPaginated) {
      for (const f of frozenFilters) {
        params.append('filter_column', f.library_column);
        params.append('filter_values', f.values.join(','));
      }
    }
    // 0.36.681 — Server-side search (only in server-paginated mode
    // AND sample mode is off). Empty search or empty column list =
    // no `q` sent, backend returns everything (which the client-side
    // small-library path then filters, if applicable). Search
    // scoping via repeated search_columns pairs matches the filter
    // param style.
    if (serverPaginated && searchActive && sampleRows == null) {
      params.set('q', searchTrimmed);
      for (const col of searchColumns) params.append('search_columns', col);
    }
    fetch(`/api/form-libraries/${encodeURIComponent(library)}/rows?${params}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (cancelled) return;
        let incoming = data.rows || [];
        // Client-side filter (small-library mode only). Server-paginated
        // mode is already filtered on the wire.
        // 0.36.687 — Empty-values filter with 'show_none' behavior
        // means "match nothing." Short-circuit to empty result set
        // if any such filter is present.
        if (!serverPaginated && frozenFilters.length > 0) {
          const hasEmptyShowNone = frozenFilters.some(
            f => (!Array.isArray(f.values) || f.values.length === 0)
              && (emptySourceBehavior || f.empty_source_behavior) === 'show_none'
          );
          if (hasEmptyShowNone) {
            incoming = [];
          } else {
            incoming = incoming.filter(row => {
              for (const f of frozenFilters) {
                if (!Array.isArray(f.values) || f.values.length === 0) {
                  // 'show_all' path — no narrowing.
                  continue;
                }
                const v = row[f.library_column];
                if (v == null) return false;
                const s = String(v);
                if (!f.values.includes(s)) return false;
              }
              return true;
            });
          }
        }
        // 0.36.681 — Client-side search (small-library mode only).
        // Substring match, case-insensitive, over selected columns.
        // Translatable cells arrive as {lang: value} dicts here when
        // no `lang` was requested (rare on public pages); to be safe
        // we support both scalar and dict values.
        if (!serverPaginated && searchActive) {
          incoming = incoming.filter(row => {
            for (const col of searchColumns) {
              const v = row[col];
              if (v == null) continue;
              if (typeof v === 'object' && !Array.isArray(v)) {
                for (const lv of Object.values(v)) {
                  if (lv != null && String(lv).toLowerCase().includes(searchLower)) return true;
                }
              } else {
                if (String(v).toLowerCase().includes(searchLower)) return true;
              }
            }
            return false;
          });
        }
        // 0.44.67 - Filtered on the stored values above; shown with the
        // date columns in the Public site format (stored as <col>_iso).
        setPrimaryTypes(data.column_types || null);
        setPrimaryRows(sampleRows != null && sampleRows > 0
          ? incoming.slice(0, sampleRows)
          : incoming);

        // 0.36.682 (hotfix) — Publish the ACTUAL post-filter count
        // to PaginationContext. Two sources depending on mode:
        //   - server-paginated: data.total is the backend-computed
        //     total AFTER the filter_column / q are applied
        //   - small library: incoming.length AFTER client-side
        //     filter and search
        // Skip in canvas sample mode (no meaningful paginator on
        // the canvas), and when reportRowCount isn't available
        // (canvas has no PaginationProvider wrapper).
        if (sampleRows == null && ctx && ctx.reportRowCount && tableName) {
          const effectiveTotal = serverPaginated
            ? (Number(data.total) || 0)
            : incoming.length;
          ctx.reportRowCount(tableName, effectiveTotal);
        }
        // 0.36.703 / 0.36.716 — Capture the effectiveTotal in state
        // so the selection publisher below can expose it as
        // `${element_id}__total`. Only update the state when SEARCH
        // is empty — total should reflect the filter-only set, not
        // filter+search. Search is a visual spotlight; during a
        // search, published __total keeps the last-known filter
        // total so downstream consumers (e.g. a title showing
        // "3 of 120 selected") stay stable while the citizen
        // narrows visible rows via typing. Sample mode skips.
        if (sampleRows == null) {
          const effectiveTotal = serverPaginated
            ? (Number(data.total) || 0)
            : incoming.length;
          if (!searchTrimmed) {
            setEffectiveTotalRows(effectiveTotal);
          }
        }
      })
      .catch(err => { if (!cancelled) setRowsError(String(err.message || err)); })
      .finally(() => { if (!cancelled) setRowsLoading(false); });
    return () => { cancelled = true; };
  // 0.36.697 — Deps intentionally exclude reference-unstable
  // values (`activePrimaryFilters` array, `ctx` object, `tableName`
  // string) that used to be here. They caused a refetch every
  // parent render because:
  //   - PageContentRenderer builds a fresh `filterValues` array
  //     each render; `activePrimaryFilters` (useMemo on it) then
  //     produces a new array reference each render even when
  //     content is identical.
  //   - PaginationContext's provider may hand back a new object
  //     each render depending on how children consume it.
  //   - tableName is stable but pulled from the same source as
  //     ctx.
  // On a 10K row library, each spurious fetch takes hundreds of
  // ms — cascading into 20+ seconds of load time.
  // `activePrimaryFiltersKey` (JSON string) already covers filter
  // content changes; nothing inside this effect uses `ctx` or
  // `tableName` directly (ctx.reportRowCount is called through
  // its own effect below).
  // 0.36.717 — Dep is `frozenFiltersKey`, not the live key, so the
  // fetch effect doesn't re-run while search is active even if the
  // live filter identity changes. When search clears, frozen keys
  // catch up and this effect re-runs once with the settled state.
  }, [library, meta, language, serverPaginated, currentPage, perPage, sampleRows,
      frozenFiltersKey,
      searchActive, searchTrimmed, searchLower, searchColumns.join(',')]);

  // 0.36.679 — When filters change in server-paginated mode, reset
  // the paginator to page 1. Otherwise a user on page 5 changes a
  // source dropdown, gets a filtered result with only 2 pages, and
  // the display goes to "Page 5 of 2" / empty rows. Small-library
  // mode doesn't need this because there's no server pagination
  // state — the client-side filter runs on the full row list.
  //
  // 0.36.681 — Same reset applies when the search text changes.
  // 0.36.717 — Watches frozenFiltersKey (not the live key) so that
  // filter changes during search don't cause a paginator reset.
  // Fetch effect uses the same key; the paginator only matters
  // when a fetch is about to hit page 1.
  const prevFilterKeyRef = useRef(frozenFiltersKey);
  const prevSearchRef = useRef(searchTrimmed);
  useEffect(() => {
    const filterChanged = prevFilterKeyRef.current !== frozenFiltersKey;
    const searchChanged = prevSearchRef.current !== searchTrimmed;
    if (!filterChanged && !searchChanged) return;
    prevFilterKeyRef.current = frozenFiltersKey;
    prevSearchRef.current = searchTrimmed;
    if (serverPaginated && ctx?.update && tableName) {
      ctx.update(tableName, { currentPage: 1 });
    }
  // 0.36.697 — ctx and tableName excluded for the same reason as
  // the fetch effect above: closure captures them at render time,
  // and their identity churn used to trigger reset every parent
  // render.
  }, [frozenFiltersKey, searchTrimmed, serverPaginated]);

  // --- Join fetches ---
  //
  // For each join, collect the set of primary_row[on_primary] values
  // from the currently-loaded primary rows, then fetch matching rows
  // from the joined library via the cascade endpoint.
  //
  // Stored as `joinLookups[joinId] = { <on_joined_value>: row }` so
  // materialisation is an O(1) lookup per cell.
  //
  // Fires when primary rows change (page nav in server-paginated
  // mode, or the initial load-all). Cascades keyed by a stable
  // string of the distinct on_primary values so we don't refetch
  // when values are the same.
  const [joinLookups, setJoinLookups] = useState({});
  const [joinsError, setJoinsError] = useState('');

  // Build a stable "distinct values per join" key. Effect fires
  // only when this key changes for a given join.
  // 0.40.65 - CHAINED joins.
  //
  // Join 1 attaches to the primary library; join 2 attaches to join 1;
  // join 3 to join 2, and so on. Previously every join attached to the
  // primary (a star), so `on_primary` always named a primary column.
  // It now names a column of that join's PARENT - the primary for the
  // first join, the preceding join's library thereafter.
  //
  // That makes resolution SEQUENTIAL: join N's key set cannot be
  // computed until join N-1 has resolved, because the key comes from
  // the parent's matched row. The old code fired every join in
  // parallel, which is no longer possible.
  //
  // A broken link partway along leaves the rest of the chain
  // unresolved for that row: later columns render blank rather than
  // throwing.
  const activeJoins = useMemo(() => joinDefs.filter(
    j => j && j.id && j.library && j.on_primary && j.on_joined
  ), [joinDefs]);

  // Signature of everything the chain depends on, so the effect reruns
  // when the config or the primary page changes but not otherwise.
  const chainSig = useMemo(() => JSON.stringify([
    activeJoins.map(j => [j.id, j.library, j.on_primary, j.on_joined]),
    primaryRows.map(r => activeJoins[0] ? r[activeJoins[0].on_primary] : null),
    language,
  ]), [activeJoins, primaryRows, language]);

  useEffect(() => {
    let cancelled = false;
    setJoinsError('');
    if (activeJoins.length === 0) {
      setJoinLookups({});
      return () => { cancelled = true; };
    }

    const fetchRows = async (libKey, column, values) => {
      if (values.length === 0) return {};
      const params = new URLSearchParams({
        filter_column: column,
        filter_values: values.join(','),
        page: '1',
        per_page: '1000',
      });
      if (language) params.set('lang', language);
      const r = await fetch(
        `/api/form-libraries/${encodeURIComponent(libKey)}/rows?${params}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const map = {};
      for (const row of (data.rows || [])) {
        const key = row[column];
        if (key != null) map[String(key)] = localizeRow(row, data.column_types);
      }
      return map;
    };

    (async () => {
      try {
        const lookups = {};
        // parentRows[i] = the row each primary row currently resolves
        // to at this depth. Starts as the primary rows themselves.
        let parentRows = primaryRows;
        for (const j of activeJoins) {
          const values = new Set();
          for (const pr of parentRows) {
            if (!pr) continue;
            const v = pr[j.on_primary];
            if (v != null && v !== '') values.add(String(v));
          }
          const map = await fetchRows(j.library, j.on_joined, [...values].sort());
          if (cancelled) return;
          lookups[j.id] = map;
          // Advance the chain: each primary row's parent for the NEXT
          // join is the row this join just matched (or null if the
          // link is broken for that row).
          parentRows = parentRows.map(pr => {
            if (!pr) return null;
            const k = pr[j.on_primary];
            if (k == null || k === '') return null;
            return map[String(k)] || null;
          });
        }
        if (!cancelled) setJoinLookups(lookups);
      } catch (err) {
        if (!cancelled) setJoinsError(String(err.message || err));
      }
    })();
    return () => { cancelled = true; };
    // 0.40.66 - Was `[JSON.stringify(joinKeys), language]`. joinKeys was
    // the parallel-fetch memo that 0.40.65 replaced with the sequential
    // chain; the memo went but this reference to it did not, so the
    // component threw "joinKeys is not defined" the moment it rendered
    // and the whole configurator page failed to load.
    //
    // chainSig already folds in the join config, the primary page's key
    // values and the language, which is exactly what this effect should
    // re-run on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainSig, fmtVersion]);

  // --- Meta lookup for join libraries (for schema labels on join
  //     columns in the column picker — actually not needed at
  //     render time; the header pull uses el.library_columns' own
  //     `header` field. So we don't need to fetch join /meta at
  //     runtime. Left as a comment for the configurator's benefit.)

  // --- Materialise headers + rows ---

  const primaryColumnLookup = useMemo(() => {
    if (!meta) return {};
    const map = {};
    for (const c of meta.columns || []) map[c.name] = c;
    return map;
  }, [meta]);

  const materializedHeaders = useMemo(() => {
    // 0.40.06 - A header override may be an =lib(...) / =expression
    // string; resolveHeader (from the caller) evaluates it. Applied
    // ONLY to the admin-set override, never to the library's own
    // column label, so a column name that happens to start with "="
    // is never treated as code.
    const resolve = (v) => {
      if (typeof resolveHeader !== 'function') return v;
      const out = resolveHeader(v);
      // Fall back to the raw text if the expression yields nothing
      // (library still loading, bad reference) - an empty header is
      // worse than a technical one.
      return (out === undefined || out === null || out === '') ? v : out;
    };
    return pickedColumns.map(p => {
      if (p.header && p.header.trim()) return resolve(p.header);
      // Fall back: primary column's schema label, or the raw name.
      // For joined columns we don't have schema metadata at runtime
      // (would need a per-join /meta fetch), so just show the
      // column name. Admin can set a header override in the
      // configurator to get a nicer label.
      if (!p.library_ref || p.library_ref === 'primary') {
        return primaryColumnLookup[p.column]?.label || p.column;
      }
      return p.column;
    });
  }, [pickedColumns, primaryColumnLookup, resolveHeader]);

  const materializedRows = useMemo(() => {
    // 0.40.95 - "Show no rows" while the search box is empty. Applied
    // HERE rather than in the fetch pipeline because that branch runs
    // only for client-paginated tables; a server-paginated one would
    // have ignored the setting entirely. Gating the materialised rows
    // covers both.
    if (searchBlocksAll) return [];
    const cell = (v) => {
      if (typeof resolveCell !== 'function' || typeof v !== 'string') return v;
      if (!v.startsWith('=') && !/\{[A-Za-z0-9_-]+(\[\d*\])*(\.|\})/.test(v)) return v;
      const out = resolveCell(v);
      return out == null ? '' : String(out);
    };
    // 0.46.30 - Hide a row when a required cell is empty.
    const _req = Array.isArray(el.col_required) ? el.col_required : null;
    return filterRequiredRows(primaryRows.map((rawRow, ri) => {
      const _key = rawRow && rawRow.id != null ? String(rawRow.id) : String(ri);
      const _ed = rowEdit && rowEdit.edits ? rowEdit.edits[_key] : null;
      const primaryRow = _ed ? { ...rawRow, ..._ed } : rawRow;
      const _cells = pickedColumns.map(p => cell((() => {
        // 0.40.71 - Extra column: belongs to no library, so there
        // is no row to look it up in. Its cells are filled manually,
        // which nothing in the renderer does - so it renders empty
        // here by design. The 0.40.69 fixed/expression sources were
        // removed: a computed value cannot be sorted, searched or
        // filtered, because all three run in the database over
        // columns that actually exist.
        if (p.library_ref === 'extra') return '';
        // Primary column: direct pull.
        if (!p.library_ref || p.library_ref === 'primary') {
          const v = primaryRow[p.column];
          return v == null ? '' : String(v);
        }
        // Joined column: look up via join's on_primary → on_joined.
        // 0.40.65 - Walk the CHAIN to this join's row. Each step keys
        // off the row the previous step resolved to, not the primary
        // row - so a column from join 3 is reached primary -> 1 -> 2
        // -> 3. A broken link anywhere along the way yields '' rather
        // than silently falling back to the primary, which would show
        // a plausible-looking wrong value.
        const chainIdx = activeJoins.findIndex(j => j.id === p.library_ref);
        if (chainIdx < 0) return '';
        let row = primaryRow;
        for (let k = 0; k <= chainIdx; k++) {
          const j = activeJoins[k];
          const key = row[j.on_primary];
          if (key == null || key === '') return '';
          row = joinLookups[j.id]?.[String(key)];
          if (!row) return '';
        }
        const v = row[p.column];
        return v == null ? '' : String(v);
      })()));
      return rowEdit ? [..._cells, rowEdit.cell(primaryRow, _key)] : _cells;
    }), _req, !!el.hide_rows_missing_required);
  }, [primaryRows, pickedColumns, joinDefs, activeJoins, joinLookups, searchBlocksAll, resolveCell, el.col_required, el.hide_rows_missing_required, rowEdit]);
  // 0.46.30 - With rows hidden, the paginator counts the rows shown
  // (all rows are here when the library is small; a large library is
  // paged on the server, and a page then simply shows fewer rows).
  useEffect(() => {
    if (!el.hide_rows_missing_required || serverPaginated || sampleRows != null) return;
    if (ctx && ctx.reportRowCount && tableName) ctx.reportRowCount(tableName, materializedRows.length);
  }, [el.hide_rows_missing_required, serverPaginated, sampleRows, ctx, tableName, materializedRows.length]);

  // 0.36.673 — Column widths projected from library_columns[i].width_px.
  // Undefined widths render as auto (browser default). Set via
  // drag-resize on the editor canvas; runtime + preview surfaces
  // just display them.
  //
  // 0.36.675 — This useMemo MUST live above the early returns below.
  // Previously it was placed after the guard rails; on first render
  // the early returns fired (meta still loading) and this hook was
  // skipped; on the second render (data loaded) the hook ran →
  // hook count changed between renders → hard crash / white page.
  // Same rules-of-hooks fix as the 0.36.674 pass in AdminFormEditor.
  const colWidths = useMemo(
    () => {
      const w = pickedColumns.map(p => p.width_px || null);
      return rowEdit ? [...w, rowEdit.isIcon ? 56 : null] : w;
    },
    [pickedColumns, !!rowEdit, rowEdit && rowEdit.isIcon]
  );


  // 0.36.677 — Per-column sortability, projected from
  // library_columns[i].sortable. Undefined = sortable (default).
  // MUST live above the early returns below (rules of hooks — same
  // pattern as colWidths).
  const sortableColumns = useMemo(
    () => {
      const sc = pickedColumns.map(p => p.sortable !== false);
      return rowEdit ? [...sc, false] : sc;
    },
    [pickedColumns, !!rowEdit]
  );

  // 0.36.682 — Row keys derived from selection_id_columns. Multi-
  // column identity → composite key joined by ASCII unit separator
  // (\x1f), safe against any admin-facing column value. When no
  // identity columns are picked, rowKeys stays null → PageTable
  // falls back to positional keys (row-index / originalIdx).
  const rowKeys = useMemo(() => {
    // 0.40.75 - Identity is ALWAYS the row's own `id`.
    //
    // Column-derived keys cannot be made safe. Two rows that are
    // identical in every column are indistinguishable to any
    // composite of those columns, so the two collide and become one
    // selection - ticking one ticks both, and one of them vanishes
    // from the payload. Adding more columns does not fix it; the
    // duplicate case defeats the whole approach.
    //
    // `id` is the DB primary key, present on every row, unique by
    // construction, hidden from the column picker, and stable across
    // the refetch that sorting, filtering and paging each trigger.
    //
    // selection_id_columns is no longer consulted. It was only ever
    // the KEY - the payload has always published full row objects
    // (primaryRows.filter(...)), so every business column a
    // downstream consumer needs is already in the data. Nothing is
    // lost by ignoring it, and the collision goes away.
    //
    // Rows without an `id` fall back to positional keys, as before.
    const hasId = primaryRows.length > 0 && primaryRows[0]
      && primaryRows[0].id != null;
    if (!hasId) return null;
    return primaryRows.map(row => String(row.id));
  }, [primaryRows]);

  // 0.36.692 — Selection state received from PageTable, and
  // publisher effect. Whenever the state or the row data changes,
  // map selection → concrete full-row objects and publish under
  // el.element_id via onSelectionPublish.
  //
  // Rules:
  //   - No element_id → nothing published.
  //   - No publisher callback → nothing published.
  //   - 'page' mode: published rows = primaryRows filtered by
  //     explicitlyChecked. Multi-page picks work because
  //     explicitlyChecked is absolute-keyed across pages, but
  //     primaryRows in server-paginated mode is only the current
  //     page's rows — so cross-page picks that aren't currently
  //     rendered won't appear in the published set. For small
  //     libraries (all rows loaded), full multi-page selection
  //     works. This matches the "page-mode selection only" scope
  //     confirmed by admin for this ship.
  //   - 'all' mode + allActive: for small libraries, publish
  //     (primaryRows - explicitlyUnchecked). For server-paginated
  //     large libraries, skip publishing (would require resolve-
  //     selection endpoint; queued for a later ship).
  const [selectionState, setSelectionState] = useState({
    effectiveMode: 'page',
    allActive: false,
    explicitlyChecked: new Set(),
    explicitlyUnchecked: new Set(),
  });
  // 0.36.692 — Publisher held in a ref so the effect below can call
  // it without listing it as a dep. PageContentRenderer /
  // AdminFormPreview may pass a fresh callback each render (they
  // build one from ctx.updateValue / setValues). Depending on the
  // publisher's identity would trigger an infinite loop:
  // publish → state change → re-render → new publisher ref → effect
  // → publish again. Instead, we snapshot it and let the effect
  // fire only when the actual selection state or row data changes.
  const publisherRef = useRef(onSelectionPublish);
  useEffect(() => { publisherRef.current = onSelectionPublish; },
    [onSelectionPublish]);
  useEffect(() => {
    const publish = publisherRef.current;
    if (!el.element_id || !publish) return;

    // 0.36.703 — Compute selection count and total. Published
    // alongside the selection rows so downstream elements (and
    // future value_display) can read them via convention:
    //   ${element_id}          — array of full row objects
    //   ${element_id}__total   — post-filter/search total
    //   ${element_id}__selected_count — currently-selected count
    //
    // 'all' mode + allActive counts everything matching filter
    // + search minus explicit unchecks — even for large libraries,
    // where the picked-rows array is empty (would require the
    // resolve-selection endpoint). The count is still meaningful.
    const totalCount = effectiveTotalRows;

    if (!Array.isArray(rowKeys) || rowKeys.length === 0) {
      publish(el.element_id, []);
      publish(`${el.element_id}__total`, totalCount);
      publish(`${el.element_id}__selected_count`, 0);
      publish(`${el.element_id}__all_selected`, false);
      publish(`${el.element_id}__any_selected`, false);
      // 0.36.717 — Search-active flag. Admin can key rules off this
      // to hide/lock filter-source elements while the citizen has a
      // search running (paired with the fetch-payload freeze in the
      // fetch effect, this locks the underlying row set for search
      // duration).
      publish(`${el.element_id}__search_active`, !!searchTrimmed);
      return;
    }
    let picked = [];
    let selectedCount = 0;
    if (selectionState.effectiveMode === 'all' && selectionState.allActive) {
      // 'all' semantics: everything matches minus unchecks.
      selectedCount = Math.max(
        0, totalCount - selectionState.explicitlyUnchecked.size
      );
      if (serverPaginated) {
        // Rows not materializable without resolve-selection; count
        // still meaningful and published.
        publish(el.element_id, []);
      } else {
        picked = primaryRows.filter(
          (_row, i) => !selectionState.explicitlyUnchecked.has(rowKeys[i])
        );
        publish(el.element_id, picked);
      }
    } else {
      picked = primaryRows.filter(
        (_row, i) => selectionState.explicitlyChecked.has(rowKeys[i])
      );
      selectedCount = selectionState.explicitlyChecked.size;
      publish(el.element_id, picked);
    }
    publish(`${el.element_id}__total`, totalCount);
    publish(`${el.element_id}__selected_count`, selectedCount);
    // 0.36.711 — Convenience booleans so rules can use `equals true`
    // without needing a field-to-field comparison operator.
    //   __all_selected: true when every row matching current
    //                   filter+search is selected (count == total).
    //                   Falsy when total is 0 (nothing to select).
    //   __any_selected: true when at least one row is selected.
    publish(`${el.element_id}__all_selected`,
            totalCount > 0 && selectedCount === totalCount);
    publish(`${el.element_id}__any_selected`, selectedCount > 0);
    // 0.36.717 — Search-active flag; see the early-return path above.
    publish(`${el.element_id}__search_active`, !!searchTrimmed);
  }, [selectionState, primaryRows, rowKeys, el.element_id,
      serverPaginated, effectiveTotalRows, searchTrimmed]);

  // 0.36.682 — Normalise selectionMode. The source element's raw
  // value can be 'page', 'all', anything else, or undefined. Only
  // 'all' switches to that mode; everything else defaults to
  // 'page'. Matches the "silent default" behavior we agreed on.
  const selectionMode = (selectionModeValue === 'all') ? 'all' : 'page';

  // --- Guard rails / early returns ---

  if (!library || pickedColumns.length === 0) {
    return (
      <div style={{
        padding: 16, textAlign: 'center', color: 'var(--color-text-600)',
        fontStyle: 'italic', border: '1px dashed var(--color-text-400)',
        borderRadius: 4,
      }}>
        {!library
          ? 'This table is set to Library source, but no library is selected.'
          : 'No columns selected. Open the table properties to pick columns.'}
      </div>
    );
  }

  if (metaError) {
    return (
      // 0.44.74 - The canonical Henaket alert, not a hand-made box.
      <Alert variant="error">
        Failed to load library <strong>{library}</strong>: {metaError}
      </Alert>
    );
  }

  if (!meta) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-600)' }}>
        Loading library…
      </div>
    );
  }

  if (rowsError) {
    return (
      <Alert variant="error" message={`Failed to load rows: ${rowsError}`} />
    );
  }

  return (
    <>
      {joinsError && (
        <Alert variant="error"
               message={`Join fetch failed: ${joinsError}. Joined columns will show empty values.`} />
      )}
      <PageTable
        name={tableName}
        /* 0.46.09 - The header row and lines, as set on the element. */
        showHeader={!el.hide_header}
        hideTopBottomLines={edgeLinesOf(el).topBottom}
        hideSideLines={edgeLinesOf(el).sides}
        hideVerticalLines={!!el.hide_vertical_lines}
        hideHorizontalLines={!!el.hide_horizontal_lines}
        headerWeight={el.header_weight || ''}
        headerColor={el.header_color || ''}
        tableColors={el}
        columnTextStyles={Array.isArray(el.col_text_styles) ? el.col_text_styles : null}
        colAlign={Array.isArray(el.col_align) ? el.col_align : null}
        checkAlign={el.check_col_align || null}
        editAlign={el.edit_col_align || null}
        /* 0.46.22 - Column sizes for large and small screens. */
        colSizes={Array.isArray(el.col_sizes) ? el.col_sizes : null}
        lastColRest={lastColRestOf(el)}
        stackLarge={!!el.stack_large}
        stackSmall={!!el.stack_small}
        headers={rowEdit ? [...materializedHeaders, rowEdit.header || ''] : materializedHeaders}
        rows={materializedRows}
        rowKeys={rowKeys}
        fitLastColumn={!!rowEdit}
        selectionMode={selectionMode}
        // 0.36.692 — PageTable notifies us of selection changes.
        // We wire it directly to setSelectionState so the publisher
        // effect above runs on any change.
        onSelectionChange={setSelectionState}
        externalCommand={externalCommand}
        // 0.36.716 / 0.36.717 — Split context keys.
        // filterContextKey drives reconciliation in PageTable.
        // Uses the FROZEN key so reconciliation doesn't fire while
        // search is active (any queued filter changes will apply as
        // one settle when search clears).
        filterContextKey={frozenFiltersKey}
        searchContextKey={searchTrimmed}
        sortable={sortable !== false && !serverPaginated && sampleRows == null}
        checkable={!!checkable}
        width={width || 1168}
        // 0.36.676 — In sample mode we've fetched exactly `sampleRows`
        // rows; PageTable's internal slicing would try to slice again
        // from a stale PaginationContext state and end up with 0 rows.
        // Set preSliced so it treats what we handed it as final.
        preSliced={serverPaginated || sampleRows != null}
        colWidths={colWidths}
        sortableColumns={sortableColumns}
        resizableColumns={canvasResize?.resizableColumns || null}
        onColumnResize={canvasResize?.onColumnResize || null}
        resizeHandlesVisible={!!canvasResize?.visible}
        // 0.36.685 → 0.37.87 — Was: `sampleRows != null` so
        // only canvas mode wrapped headers. Now: always wrap.
        // Runtime and preview were previously producing a
        // horizontal scrollbar whenever a header text was
        // longer than its column's width (admin report). Same
        // change as the manual-table renderTable branch this
        // ship — .headerCell already has `vertical-align: top`
        // so wrapped multi-line headers stay anchored cleanly
        // and the row grows to fit.
        wrapAllHeaders={true}
      />
      {/* 0.36.676 — Overflow note in sample (canvas) mode. Matches
        * the "+ N more rows" convention used for manual tables on
        * the canvas. Only shown when the library actually has more
        * rows than we're displaying. */}
      {sampleRows != null && (meta?.total_rows || 0) > materializedRows.length && (
        <div style={{
          marginTop: 4,
          padding: '4px 10px',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-600)',
          textAlign: 'center',
          fontStyle: 'italic',
          background: 'var(--color-bg-alt)',
        }}>
          + {(meta.total_rows || 0) - materializedRows.length} more row
          {((meta.total_rows || 0) - materializedRows.length) === 1 ? '' : 's'}
          {' '}(canvas shows first {sampleRows} for adjustment)
        </div>
      )}
    </>
  );
}
